import { Editor } from 'slate';

import deserializeSlateClipboardHtml from '../../../services/deserializeSlateClipboardHtml';

export interface IClipboardEditor extends Editor {
    insertData: (data: DataTransfer) => void,
}

interface IClipboardPasteEvent {
    clipboardData: DataTransfer | null,
    preventDefault: () => void,
}

/**
 * Rich clipboard payloads are most reliable on the native `paste` event. Route
 * that payload through the editor's insertData pipeline before browsers fall
 * back to a reduced beforeinput representation.
 */
export function handleEditorClipboardPaste({
    editor,
    event,
}: {
    editor: Editor,
    event: IClipboardPasteEvent,
}): boolean {
    if (!event.clipboardData) {
        return false;
    }

    event.preventDefault();
    (editor as IClipboardEditor).insertData(event.clipboardData);
    return true;
}

/**
 * External rich-text pastes should preserve the subset of formatting this
 * editor already knows how to render, while internal Slate copies should keep
 * their higher-fidelity fragment path untouched.
 */
export const withRichTextClipboard = (editor: Editor, _readOnly?: boolean): Editor => {
    const clipboardEditor = editor as IClipboardEditor;
    const { insertData } = clipboardEditor;

    // eslint-disable-next-line no-param-reassign
    clipboardEditor.insertData = (data: DataTransfer): void => {
        const clipboardTypes = Array.from(data.types || []).map((type) => type.toLowerCase());
        const containsSlateFragment = clipboardTypes.some((type) => type.includes('slate-fragment'));
        if (containsSlateFragment) {
            insertData(data);
            return;
        }

        const html = data.getData('text/html');
        if (!html.trim()) {
            insertData(data);
            return;
        }

        const richTextFragment = deserializeSlateClipboardHtml({
            html,
        });
        if (!richTextFragment) {
            insertData(data);
            return;
        }

        clipboardEditor.insertFragment(richTextFragment);
    };

    return clipboardEditor;
};
